<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import moment from "moment-timezone";

import { ModalName } from '@/constants/modals';
import { capitalize } from "@/helpers/string";
import { useAuthStore } from "@/modules/auth/store/authStore";
import workerService from "@/modules/worker/services/workerService";
import { useConfigurationStore } from "@/store/configurationStore";

import type { IHeader } from '@/types/view';
import type { IInterimDB } from '@/types/worker';

import Button from "@/dsComponents/buttons/Button.vue";
import ControlledDropdown from "@/dsComponents/Dropdown/ControlledDropdown.vue";
import { useModalStore } from "@/dsComponents/modals/modalStore";

import Icon from "@/components/Icon.vue";
// Components
import AvailabilityZone from "@/components/interim/AvailabilityZone.vue";

import Pulse from '../loader/Pulse.vue';

interface Props {
    interim: IInterimDB;
    mode?: string;
    readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    mode: "",
    readonly: false,
});
const { t } = useI18n();
const authStore = useAuthStore();
const configStore = useConfigurationStore();
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const toaster = useToast();
const modalStore = useModalStore();

const emit = defineEmits(["close"]);

// Store getters
const isSupervisor = computed(() => authStore.isSupervisor);
const unavailabilitiesReasons = computed(() => configStore.unavailabilityReasonsOptions);

// State
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const timeout = ref<any>(0);
const loading = ref(false);
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const unavailabilities = ref<any>([]);
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const dateRange = ref<any>({
    from: moment().startOf("month").valueOf(),
    to: moment().endOf("month").valueOf(),
});
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const globalErrors = ref<any>([]);
const innerLoading = ref(false);

const isEditMode = computed(() => props.mode === "edit");

const headers = computed((): IHeader[] => {
    return moment.weekdays(true).map((d: string) => ({ label: capitalize(d) ?? "" }));
});

const ranges = computed(() => {
    // biome-ignore lint/suspicious/noImplicitAnyLet: Init Biome
    let fromDate, toDate;

    if (typeof dateRange.value.from === "number") {
        fromDate = dateRange.value.from;
        toDate = dateRange.value.to;
    } else {
        fromDate = dateRange.value.from.valueOf();
        toDate = dateRange.value.to.valueOf();
    }

    return {
        from: fromDate,
        to: toDate,
    };
});

/**
 * Generates a 2D array representing a monthly calendar
 * Each row contains 7 days (one week)
 * Days outside the current month are marked as "out"
 * Unavailabilities are associated with corresponding days
 */
const rows = computed(() => {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    const rowsArray: any[] = [];
    const fromDate = moment(ranges.value.from);
    const daysInMonth = fromDate.daysInMonth();
    const firstWeekday = fromDate.weekday(); // 0-6 (Sunday-Saturday)

    // Calculate the first day to display (may be from previous month)
    const firstDay = firstWeekday === 0 ? fromDate : fromDate.clone().subtract(firstWeekday, "day");

    // Total number of days to display (month + days from previous month)
    const totalDays = daysInMonth + firstWeekday;

    // Generate weeks
    for (let dayIndex = 0; dayIndex < totalDays; dayIndex++) {
        const weekIndex = Math.floor(dayIndex / 7);
        const cellDate = firstDay.clone().add(dayIndex, "day");

        // Initialize week if needed
        if (!rowsArray[weekIndex]) {
            rowsArray[weekIndex] = [];
        }

        // Find unavailabilities for this day
        const unavailability =
            cellDate.month() === fromDate.month() && unavailabilities.value
                // biome-ignore lint/suspicious/noExplicitAny: Init Biome
                ? unavailabilities.value.find((u: any) => u.date && isTodayOrDate(cellDate.valueOf(), u.date))
                : null;

        // Add day to calendar
        rowsArray[weekIndex].push({
            timestamp: cellDate.valueOf(),
            unavailabilities: unavailability,
            out: dayIndex < firstWeekday, // Mark as "out" if before month start
        });
    }

    // Complete last week if needed
    const lastWeek = rowsArray[rowsArray.length - 1];
    if (lastWeek && lastWeek.length < 7) {
        const remainingDays = 7 - lastWeek.length;
        for (let i = 0; i < remainingDays; i++) {
            const cellDate = firstDay.clone().add(totalDays + i, "day");
            lastWeek.push({
                timestamp: cellDate.valueOf(),
                unavailabilities: null,
                out: true,
            });
        }
    }
    return rowsArray;
});

const range = computed(() => {
    const fromDate = moment(dateRange.value.from);
    return { month: capitalize(fromDate.format("MMMM")), year: fromDate.format("YYYY") };
});

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const getUnavailabilityReason = (cell: any) => {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    const unavailability: any = cell.unavailabilities?.zones;
    if (!unavailability) return "";
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    const reason = unavailabilitiesReasons.value.find((unavailabilityReason: any) => unavailabilityReason.value === unavailability.reason);
    return reason?.label ?? unavailability.reason ?? "";
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const getUnavailabilityComment = (cell: any) => {
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    const unavailability: any = Object.values(cell.unavailabilities || {}).find((zone: any) => zone.comment);
    return unavailability?.comment;
};

const subDate = () => {
    dateRange.value.from = moment(dateRange.value.from).subtract(1, "month").startOf("month").valueOf();
    dateRange.value.to = moment(dateRange.value.to).subtract(1, "month").endOf("month").valueOf();
};

const addDate = () => {
    dateRange.value.from = moment(dateRange.value.from).add(1, "month").startOf("month").valueOf();
    dateRange.value.to = moment(dateRange.value.to).add(1, "month").endOf("month").valueOf();
};

const isTodayOrDate = (timestamp: number, d?: number) => {
    const date = moment(timestamp);
    const today = moment(d);

    return date.date() === today.date() && date.month() === today.month() && date.year() === today.year();
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const isEvoliaUnavailability = (cell: any) => {
    if (!cell.unavailabilities?.zones) {
        return false;
    }
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    return Object.values(cell.unavailabilities.zones).some((zone: any) => zone.evoliaId);
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const getEvoliaAvailabilityReason = (cell: any) => {
    if (!cell.unavailabilities?.zones) return {};
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    const unavailability: any = Object.values(cell.unavailabilities.zones).find((zone: any) => zone.reason);
    return unavailability?.reason || {};
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const haveUnavailabilitiesReason = (cell: any) => {
    if (!cell.unavailabilities?.zones) return false;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    return cell.unavailabilities?.reason && !Object.values(cell.unavailabilities.zones).some((zone: any) => zone.evoliaId);
};

const openLongUnav = () => {
    modalStore.actions.open(ModalName.ProfileUnavailabilityModal, {
        interimId: props.interim.id,
        onRefresh: () => handleRefresh(),
    });
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const toggle = (cell: any, context: string) => {
    // biome-ignore lint/suspicious/noImplicitAnyLet: Init Biome
    let unavailabilityValue;

    if (isEditMode.value) {
        unavailabilityValue = !cell.unavailabilities.zones[context].value;
        if (cell.unavailabilities.zones[context].evoliaId) {
            cell.unavailabilities.zones[context] = {
                ...cell.unavailabilities.zones[context],
                value: unavailabilityValue,
            };
        } else {
            unavailabilityValue = !cell.unavailabilities.zones[context];
            cell.unavailabilities.zones[context] = unavailabilityValue;
        }

        updateUnav({
            date: moment(cell.timestamp).utc(true).toISOString(),
            ...cell.unavailabilities.zones,
            [context]: unavailabilityValue,
        });
    }
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const updateUnav = async (data: any) => {
    innerLoading.value = true;
    try {
        await workerService.saveUnavailability({
            interimId: props.interim?.id,
            data,
        });
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    } catch (e: any) {
        console.error(e);
    } finally {
        innerLoading.value = false;
    }
};

const init = async () => {
    loading.value = true;
    globalErrors.value = [];
    try {
        unavailabilities.value = await workerService.getInterimUnavailabilities({
            interimId: props.interim.id,
            ranges: ranges.value,
        });
    } catch (e) {
        console.error(e);
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        const error = e as any;
        if (error?.fullErr?.response?.status === 500) {
            globalErrors.value.push({ id: "Global.ServerError" });
        }
    }
    loading.value = false;
};

const timedInit = () => {
    if (timeout.value) {
        clearTimeout(timeout.value);
    }

    timeout.value = setTimeout(() => {
        init();
    }, 0);
};

watch(dateRange, () => timedInit(), { deep: true });

watch(
    () => props.interim,
    () => init()
);

onBeforeMount(() => {
    init();
});

const open = ref<string[]>([]);

const handleChange = (eventName: string, index: string) => {
    if (eventName === "mouseenter") {
        open.value.push(index);
    }
    if (eventName === "mouseleave") {
        open.value = open.value.filter((i) => i !== index);
    }
};

const handleRefresh = async () => {
    await init();
};
</script>
<template>
    <div :class="['InterimAvailabilities', mode]">
        <Pulse v-if="loading" />
        <template v-else-if="!globalErrors.length">
            <div class="top">
                <div class="date">
                    <div class="arrow left" @click="subDate">
                        <icon icon="leftArrow" />
                    </div>
                    <div class="middle">
                        <div class="month">{{ range.month }}</div>
                        <div class="year">{{ range.year }}</div>
                    </div>
                    <div class="arrow right" @click="addDate">
                        <Icon icon="rightArrow" />
                    </div>
                </div>
                <div class="action">
                    <Button v-if="!readonly && !isSupervisor" @click="openLongUnav" leftIcon="circle-plus" size="sm">
                        {{ t("page.interim.addLongUnav") }}
                    </Button>
                </div>
            </div>
            <div class="rows">
                <div class="rows-heads">
                    <div class="rows-head" v-for="(header, index) in headers" :key="`head${index}`">
                        <div>{{ header.label }}</div>
                    </div>
                </div>
                <div class="rows-rows">
                    <div class="rows-row" v-for="(row, index2) in rows" :key="`row${index2}`">
                        <div class="rows-cell" v-for="(cell, index3) in row" :key="`mission${index3}`">
                            <div :class="['mission-month-cell', { today: isTodayOrDate(cell.timestamp) }, { out: cell.out }]">
                                <div v-if="!isEvoliaUnavailability(cell) && haveUnavailabilitiesReason(cell)" class="cell-header">
                                    <span>{{ moment(cell.timestamp).date() }}</span>
                                    <span class="txt-evolia">Indispo. Longue</span>
                                    <ControlledDropdown
                                        @change="(eventName) => handleChange(eventName, `${index2}-${index3}`)"
                                        :open="open.includes(`${index2}-${index3}`)"
                                        width="400px"
                                    >
                                        <template #trigger-zone>
                                            <i class="ti ti-info-circle" />
                                        </template>
                                        <template #dropdown-content>
                                            <div class="dropdown-content">
                                                <div class="main">{{ t("page.interim.addLongUnav") }}</div>
                                                <div class="item">{{ getUnavailabilityReason(cell) }}</div>
                                                <div class="item">{{ getUnavailabilityComment(cell) }}</div>
                                            </div>
                                        </template>
                                    </ControlledDropdown>
                                </div>
                                <div class="cell-header" v-else-if="isEvoliaUnavailability(cell)">
                                    <span>{{ moment(cell.timestamp).date() }}</span>
                                    <span class="txt-evolia">Indispo. Evolia</span>
                                    <ControlledDropdown
                                        @change="(eventName) => handleChange(eventName, `${index2}-${index3}`)"
                                        :open="open.includes(`${index2}-${index3}`)"
                                        width="400px"
                                    >
                                        <template #trigger-zone>
                                            <i class="ti ti-info-circle" />
                                        </template>
                                        <template #dropdown-content>
                                            <div class="dropdown-content">
                                                <div class="main">{{ t("page.interim.evoliaUnav") }}</div>
                                                <div class="item" v-if="getEvoliaAvailabilityReason(cell).label">
                                                    {{ (getEvoliaAvailabilityReason(cell).label || "").toLowerCase() }}
                                                </div>
                                                <div class="item" v-if="getEvoliaAvailabilityReason(cell).comment">
                                                    {{ getEvoliaAvailabilityReason(cell).comment }}
                                                </div>
                                                <div class="item" v-if="getEvoliaAvailabilityReason(cell).evoliaContractId">
                                                    {{ getEvoliaAvailabilityReason(cell).evoliaContractId }}
                                                </div>
                                            </div>
                                        </template>
                                    </ControlledDropdown>
                                </div>
                                <div v-else class="cell-header">{{ moment(cell.timestamp).date() }}</div>
                                <div class="zones" v-if="cell.unavailabilities?.zones">
                                    <AvailabilityZone
                                        v-for="context in Object.keys(cell.unavailabilities.zones)"
                                        :key="`${index2}-${index3}-${context}`"
                                        :class="{ edit: isEditMode }"
                                        :context="context"
                                        :cell="cell"
                                        @toggle="toggle(cell, context)"
                                        @refresh="handleRefresh"
                                        @close="emit('close')"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <global-errors :errors="globalErrors" />
        </template>
    </div>
</template>
<style scoped lang="scss">
.InterimAvailabilities {
    .date {
        padding: 8px;
        display: flex;
        align-items: center;
        color: $black;
        font-size: 18px;
        user-select: none;
        margin-bottom: 8px;

        .middle {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 150px;
            gap: 8px;
        }
    }

    .right {
        margin-left: 8px;
    }
    .left {
        margin-right: 8px;
    }

    .arrow {
        cursor: pointer;

        &:hover {
            opacity: 0.7;
        }
    }

    .top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid $lightgrey;
        padding-bottom: 8px;
    }

    .rows {
        .rows-row,
        .rows-heads {
            display: flex;
        }

        .rows-heads {
            height: 30px;
            align-items: center;

            .rows-head {
                width: 14.28%;
                height: 100%;
                border-right: 1px solid $lightgrey;
                display: flex;
                justify-content: center;
                align-items: center;
                font-size: 12px;
                border-bottom: 1px solid $lightgrey;

                &:last-child {
                    border-right: none;
                }
            }
        }

        .rows-row {
            border-bottom: 1px solid $lightgrey;

            &:last-child {
                border-bottom: none;
            }

            .mission-month-cell {
                position: relative;
                height: 100%;
                user-select: none;
                padding: 0 7.5px;
                .client-icon {
                    position: absolute;
                    top: 0;
                    right: 0;
                    width: 18px;
                    height: 18px;

                    &.evolia {
                        svg {
                            fill: $purple-300;
                            cursor: pointer;
                        }
                    }

                    svg {
                        width: 18px;
                        height: 18px;
                        fill: $black;
                        cursor: pointer;
                    }
                }

                .cell-header {
                    width: 100%;
                    display: flex;
                    font-size: 16px;
                    padding: 4px 8px;
                    justify-content: space-between;
                    align-items: center;
                    font-weight: 700;
                    .txt-evolia {
                        @include font-size-12;
                        white-space: nowrap;
                        margin-right: 3px;
                        margin-left: 3px;
                    }
                    :deep(.Icon) {
                        margin-left: 5px;
                        &:hover {
                            opacity: 0.7;
                        }
                    }
                }

                &.out {
                    background: $lightgrey;
                    color: $white;
                }
                &.today {
                    background: $purple;
                    color: $white;
                    &.txt-evolia {
                        color: white;
                    }
                }
            }

            .rows-cell {
                width: 14.28%;
                border-right: 1px solid $lightgrey;
                text-align: center;
                position: relative;

                &:last-child {
                    border-right: none;
                }
            }
        }
    }
}

.dropdown-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px;
    .main {
        font-weight: bold;
    }
    .item {
        font-size: 12px;
    }
}
</style>
